import React, {useEffect, useState} from 'react';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedFlightToView, setSelectedFlightToView } from "_redux/slices/timeline/selected-task";

// styles
import { Spacing } from '_styles';
import { PRM_BLUE, GRAY_LIGHT, GRAY_DARK, TEXT_COLOR } from '_styles/colors';

// tremor
import { Subtitle, Title } from "@tremor/react";

// bootstrap
import { Card } from 'react-bootstrap';

function formatIsoString(isoString) {
    const date = new Date(isoString);
  
    const pad = (num) => String(num).padStart(2, '0');
  
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1); // months are 0-based
    const day = pad(date.getDate());
  
    return {
        time: `${hours}:${minutes}:${seconds}`,
        date: `${year}-${month}-${day}`
    };
}

const FlightFeedUpdates = (props) => {

    // redux
    const selectedFlightToView = useSelector(selectSelectedFlightToView);

    // local states
    const [feedUpdates, setFeedUpdates] = useState([]);
    
    // log selected flight
    useEffect(() => {
        if(selectedFlightToView && selectedFlightToView?.history){
            setFeedUpdates(selectedFlightToView.history);
        }   
    }, [selectedFlightToView])

    //#region flight feed update card + styling

    const FlightFeedUpdate = ({ update, index }) => {
        
        //#region styling
        const cardStyle = {
            marginBottom: Spacing.SCALE_16,
            border: `1px solid ${GRAY_LIGHT}`,
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            backgroundColor: 'white'
        };

        const headerStyle = {
            backgroundColor: index === 'current-update' ? '#28a745' : 
                            index === 0 ? '#6c757d' : PRM_BLUE, // Green for current, gray for initial, blue for historical
            color: 'white',
            padding: Spacing.SCALE_12,
            borderRadius: '8px 8px 0 0',
            margin: 0,
            display:'flex',
            justifyContent:'space-between',
        };

        const contentStyle = {
            padding: Spacing.SCALE_16
        };

        const infoItemStyle = {
            marginBottom: Spacing.SCALE_12
        };

        const labelStyle = {
            fontSize: '12px',
            fontWeight: '600',
            color: GRAY_DARK,
            marginBottom: '4px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        };

        const valueStyle = {
            fontSize: '14px',
            color: TEXT_COLOR,
            margin: 0,
            fontWeight: '500'
        };

        const statusBadgeStyle = {
            backgroundColor: update?.operationInfo?.statusType === 'ON_TIME' ? '#28a745' : 
                           update?.operationInfo?.statusType === 'DELAYED' ? '#ffc107' : '#dc3545',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '600'
        };
        //#endregion

        return (
            <Card style={cardStyle}>
                <Card.Header style={headerStyle}>

                    {/* update number */}
                    <p style={{ margin: 0, color: 'white', fontSize: '16px' }}>
                        {index === 'current-update' ? 'Current Status' : 
                         index === 0 ? 'Flight Created' : `Update #${index + 1}`}
                    </p>

                    {/* timestamp */}
                    <div style={{ textAlign: 'right' }}>
                        {(update?.updatedAt || update?.operationInfo?.time) ? (
                            <>
                                <p style={{ margin: 0, color: 'white', fontSize: '16px', fontWeight: '600' }}>
                                    {formatIsoString(update.updatedAt || update.operationInfo.time).time}
                                </p>
                                <p style={{ margin: 0, color: 'white', fontSize: '12px', opacity: 0.9 }}>
                                    {formatIsoString(update.updatedAt || update.operationInfo.time).date}
                                </p>
                            </>
                        ) : (
                            <p style={{ margin: 0, color: 'white', fontSize: '16px' }}>N/A</p>
                        )}
                    </div>
                </Card.Header>
                <Card.Body style={contentStyle}>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-evenly', 
                        alignItems: 'flex-start',
                        gap: Spacing.SCALE_16
                    }}>
                        <div style={{ 
                            flex: 1, 
                            textAlign: 'flex-start',
                            backgroundColor: update?.gateChanged ? '#fff3cd' : 'transparent',
                            border: update?.gateChanged ? '2px solid #ffc107' : '2px solid transparent',
                            borderRadius: '6px',
                            padding: update?.gateChanged ? '8px' : '0px',
                            transition: 'all 0.2s ease'
                        }}>
                            <div style={labelStyle}>
                                Gate {update?.gateChanged && <span style={{ color: '#856404', fontSize: '10px' }}>● CHANGED</span>}
                            </div>
                            <div style={valueStyle}>{update?.gate || 'N/A'}</div>
                        </div>
                        <div style={{ 
                            flex: 1, 
                            textAlign: 'flex-start',
                            backgroundColor: update?.standChanged ? '#fff3cd' : 'transparent',
                            border: update?.standChanged ? '2px solid #ffc107' : '2px solid transparent',
                            borderRadius: '6px',
                            padding: update?.standChanged ? '8px' : '0px',
                            transition: 'all 0.2s ease'
                        }}>
                            <div style={labelStyle}>
                                Stand {update?.standChanged && <span style={{ color: '#856404', fontSize: '10px' }}>● CHANGED</span>}
                            </div>
                            <div style={valueStyle}>{update?.stand || 'N/A'}</div>
                        </div>
                        <div style={{ 
                            flex: 1, 
                            textAlign: 'flex-start',
                            backgroundColor: update?.operationTimeChanged ? '#fff3cd' : 'transparent',
                            border: update?.operationTimeChanged ? '2px solid #ffc107' : '2px solid transparent',
                            borderRadius: '6px',
                            padding: update?.operationTimeChanged ? '8px' : '0px',
                            transition: 'all 0.2s ease'
                        }}>
                            <div style={labelStyle}>
                                Operating Time {update?.operationTimeChanged && <span style={{ color: '#856404', fontSize: '10px' }}>● CHANGED</span>}
                            </div>
                            <div style={valueStyle}>
                                {update?.operationInfo?.time ? (
                                    <>
                                        <div style={{ fontWeight: '600' }}>
                                            {formatIsoString(update.operationInfo.time).time}
                                        </div>
                                        <div style={{ fontSize: '12px', color: GRAY_DARK }}>
                                            {formatIsoString(update.operationInfo.time).date}
                                        </div>
                                    </>
                                ) : 'N/A'}
                            </div>
                        </div>
                        <div style={{ 
                            flex: 1, 
                            textAlign: 'flex-start',
                            backgroundColor: update?.terminalChanged ? '#fff3cd' : 'transparent',
                            border: update?.terminalChanged ? '2px solid #ffc107' : '2px solid transparent',
                            borderRadius: '6px',
                            padding: update?.terminalChanged ? '8px' : '0px',
                            transition: 'all 0.2s ease'
                        }}>
                            <div style={labelStyle}>
                                Terminal {update?.terminalChanged && <span style={{ color: '#856404', fontSize: '10px' }}>● CHANGED</span>}
                            </div>
                            <div style={valueStyle}>{update?.terminal || 'N/A'}</div>
                        </div>
                        <div style={{ flex: 1, textAlign: 'flex-start' }}>
                            <div style={labelStyle}>Status</div>
                            <div style={valueStyle}>{update?.operationInfo?.statusType || 'N/A'}</div>
                        </div>
                        <div style={{ flex: 1, textAlign: 'flex-start' }}>
                            <div style={labelStyle}>Type</div>
                            <div style={valueStyle}>{update?.operationInfo?.status || 'N/A'}</div>
                        </div>
                    </div>
                </Card.Body>

                {/* cache id in footer */}
                <Card.Footer style={{ backgroundColor: '#f8f9fa', borderTop: `1px solid ${GRAY_LIGHT}`, borderRadius: '0 0 8px 8px' }}>
                    <small 
                        style={{ 
                            color: GRAY_DARK,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            transition: 'color 0.2s ease'
                        }}
                        onClick={() => {
                            const cacheId = update?.operationInfo?.cache;
                            if (cacheId && cacheId !== 'N/A') {
                                window.open(`/api-data?cacheId=${cacheId}`, '_blank');
                            }
                        }}
                        onMouseEnter={(e) => e.target.style.color = PRM_BLUE}
                        onMouseLeave={(e) => e.target.style.color = GRAY_DARK}
                    >
                        Cache ID: {update?.operationInfo?.cache || 'N/A'}
                    </small>
                </Card.Footer>

            </Card>
        );  
    }

    const containerStyle = {
        marginBottom: Spacing.SCALE_24
    };

    const titleStyle = {
        color: PRM_BLUE,
        marginBottom: Spacing.SCALE_16,
        fontSize: '20px',
        fontWeight: '600'
    };

    const emptyStateStyle = {
        textAlign: 'center',
        padding: Spacing.SCALE_32,
        color: GRAY_DARK,
        backgroundColor: GRAY_LIGHT,
        borderRadius: '8px',
        border: `1px solid ${GRAY_LIGHT}`
    };

    //#endregion

    return (
        <div style={containerStyle}>
            {feedUpdates && feedUpdates.length > 0 ? (
                <div>
                    
                    {/* title */}
                    <Subtitle>Flight Feed Updates</Subtitle>

                    {/* body */}
                    <div className='mt-2'>

                        {/* current update */}
                        {selectedFlightToView?.operationInfo && (
                            <div style={{ marginBottom: Spacing.SCALE_24 }}>
                                <FlightFeedUpdate 
                                    key={`current-update`}
                                    index="current-update" 
                                    update={selectedFlightToView} 
                                />
                            </div>
                        )}  
                        
                        {/* updates list */}
                        <div className='mt-3'>
                            {feedUpdates.slice().reverse().map((update, index) => (
                                <FlightFeedUpdate 
                                    key={`update-${feedUpdates.length - 1 - index}`}
                                    index={feedUpdates.length - 1 - index} 
                                    update={update} 
                                />
                            ))}
                        </div>

                    </div>
                </div>
            ) : (
                <div style={emptyStateStyle}>
                    <Subtitle style={{ color: GRAY_DARK, margin: 0 }}>
                        No feed updates available for this flight.
                    </Subtitle>
                </div>
            )}
        </div>
    );
};

export default FlightFeedUpdates;
